import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { NavLink, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import AckGate from "@/components/AckGate";
import { QuotaReminder } from "@/components/QuotaReminder";
import {
  LayoutDashboard, Users, RefreshCw, FileText, CalendarClock,
  BellRing, UserCog, LogOut, Menu, IdCard, ClipboardCheck, CreditCard, FolderOpen, Handshake, Images,
  Globe,
} from "lucide-react";

const MANAGER_NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true, id: "dashboard" },
  { to: "/mia-scheda", label: "La mia scheda", icon: IdCard, id: "mia-scheda" },
  { to: "/anagrafica", label: "Anagrafica", icon: Users, id: "anagrafica" },
  { to: "/sync", label: "Sincronizza", icon: RefreshCw, id: "sync" },
  { to: "/interventi", label: "Interventi", icon: FileText, id: "interventi" },
  { to: "/turni", label: "Turni", icon: CalendarClock, id: "turni" },
  { to: "/presenze", label: "Presenze", icon: ClipboardCheck, id: "presenze" },
  { to: "/pagamenti", label: "Pagamenti", icon: CreditCard, id: "pagamenti" },
  { to: "/scadenze", label: "Avvisi scadenze", icon: BellRing, id: "scadenze" },
  { to: "/documenti", label: "Documenti", icon: FolderOpen, id: "documenti" },
  { to: "/convenzioni", label: "Convenzioni CRAL", icon: Handshake, id: "convenzioni" },
  { to: "/galleria", label: "Galleria foto", icon: Images, id: "galleria" },
  { to: "/sito/gestione", label: "Gestione sito", icon: Globe, id: "gestione-sito" },
  { to: "/utenti", label: "Utenti", icon: UserCog, id: "utenti" },
];

const TERRITORIALE_NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true, id: "dashboard" },
  { to: "/mia-scheda", label: "La mia scheda", icon: IdCard, id: "mia-scheda" },
  { to: "/interventi", label: "Interventi", icon: FileText, id: "interventi" },
  { to: "/turni", label: "Turni", icon: CalendarClock, id: "turni" },
  { to: "/presenze", label: "Presenze", icon: ClipboardCheck, id: "presenze" },
  { to: "/pagamenti", label: "Pagamenti", icon: CreditCard, id: "pagamenti" },
  { to: "/documenti", label: "Documenti", icon: FolderOpen, id: "documenti" },
  { to: "/convenzioni", label: "Convenzioni CRAL", icon: Handshake, id: "convenzioni" },
];

const GUARDIA_NAV = [
  { to: "/mia-scheda", label: "La mia scheda", icon: IdCard, id: "mia-scheda" },
  { to: "/turni", label: "I miei turni", icon: CalendarClock, id: "turni" },
  { to: "/interventi", label: "I miei verbali", icon: FileText, id: "interventi" },
  { to: "/presenze", label: "Le mie presenze", icon: ClipboardCheck, id: "presenze" },
  { to: "/pagamenti", label: "Pagamenti", icon: CreditCard, id: "pagamenti" },
  { to: "/documenti", label: "Documenti", icon: FolderOpen, id: "documenti" },
];

function NavItems({ nav, onNavigate }) {
  return (
    <nav className="flex flex-col gap-1 px-3">
      {nav.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.end}
          onClick={onNavigate}
          data-testid={`nav-${item.id}`}
          className={({ isActive }) =>
            `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
              isActive
                ? "bg-[#14382C] text-white shadow-sm"
                : "text-slate-300 hover:bg-white/10 hover:text-white"
            }`
          }
        >
          <item.icon className="h-[18px] w-[18px]" />
          {item.label}
        </NavLink>
      ))}
    </nav>
  );
}

export default function AppLayout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const nav = user?.role === "guardia" ? GUARDIA_NAV
    : ["territoriale", "com_distaccamento"].includes(user?.role) ? TERRITORIALE_NAV : MANAGER_NAV;
  const roleLabel = { admin: "Amministratore", coordinatore: "Coordinatore", territoriale: "Territoriale", com_distaccamento: "Com. Distaccamento", guardia: "Guardia" }[user?.role] || "Utente";

  const Brand = () => (
    <div className="flex items-center gap-3 px-6 py-5">
      <img
        src="/emblem.png"
        alt="Emblema associazione"
        className="h-11 w-auto object-contain"
      />
      <div className="leading-tight">
        <p className="font-heading text-[13px] font-extrabold leading-tight text-white">Associazione Nazionale Guardie Ecozoofile ODV</p>
        <p className="mt-0.5 text-[10px] uppercase tracking-wider text-slate-400">Piattaforma Operativa</p>
      </div>
    </div>
  );

  const SidebarInner = ({ onNavigate }) => (
    <div className="flex h-full flex-col bg-slate-900">
      <Brand />
      <div className="flex-1 overflow-y-auto gz-scroll py-2">
        <NavItems nav={nav} onNavigate={onNavigate} />
      </div>
      <div className="border-t border-slate-800 p-4">
        <div className="mb-3 px-1">
          <p className="truncate text-sm font-semibold text-white">{user?.name}</p>
          <p className="truncate text-xs text-slate-400">{user?.email}</p>
          <p className="mt-1 inline-block rounded bg-[#D97706] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">
            {roleLabel}
          </p>
        </div>
        <Button
          data-testid="logout-button"
          variant="outline"
          size="sm"
          className="w-full border-slate-700 bg-transparent text-slate-200 hover:bg-white/10 hover:text-white"
          onClick={async () => {
            await logout();
            navigate("/login");
          }}
        >
          <LogOut className="mr-2 h-4 w-4" /> Esci
        </Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50">
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-64 lg:block">
        <SidebarInner />
      </aside>

      <div className="lg:pl-64">
        <header className="sticky top-0 z-20 flex h-14 items-center gap-3 border-b border-slate-200 bg-white/90 px-4 backdrop-blur-md lg:hidden">
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button data-testid="mobile-menu-button" variant="ghost" size="icon">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-64 border-0 p-0">
              <SidebarInner onNavigate={() => setOpen(false)} />
            </SheetContent>
          </Sheet>
          <div className="flex items-center gap-2">
            <img
              src="/emblem.png"
              alt="Emblema associazione"
              className="h-7 w-auto object-contain"
            />
            <span className="font-heading text-xs font-bold leading-tight text-slate-900">Associazione Nazionale Guardie Ecozoofile ODV</span>
          </div>
        </header>

        <main className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8"><QuotaReminder />{children}</main>
      </div>
      <AckGate />
    </div>
  );
}
